import { checkFileExists } from "@/utils/apiFunctions";
import { io } from "socket.io-client";

export default {
	methods: {
		async calculateHash(file, { bucket, assetPath }) {
			try {
				this.progress = 1;
				this.timeRemaining = "1 sec";
				const arrayBuffer = await file.arrayBuffer();
				const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
				const hashArray = Array.from(new Uint8Array(hashBuffer));
				const fileHash = hashArray
					.map((b) => b.toString(16).padStart(2, "0"))
					.join("");

				const payload = {
					fileName: file.name,
					content: fileHash,
					size: file.size,
					bucket,
					bucketPath: `${assetPath}/${file.name}`,
				};

				const response = await checkFileExists(payload);

				if (response.status === 200) {
					const bucketPath = await this.chunkAndSendFile(
						response.data.data,
						arrayBuffer
					);
					return bucketPath;
				} else {
					throw new Error("File does not exist in the bucket");
				}
			} catch (error) {
				console.error("Error in calculateHash:", error);
				throw error;
			}
		},

		async chunkAndSendFile(uploadData, arrayBuffer) {
			return new Promise((resolve, reject) => {
				const chunkSize = 5 * 1024 * 1024; // 5MB
				const chunks = [];
				let offset = 0;

				while (offset < arrayBuffer.byteLength) {
					const end = Math.min(offset + chunkSize, arrayBuffer.byteLength);
					chunks.push(arrayBuffer.slice(offset, end));
					offset = end;
				}

				const socket = io("https://awsmultipart.hire3x.com/", {
					transports: ["websocket"],
					withCredentials: true,
				});

				let startTime = null;
				let totalUploadedSize = 0;

				const {
					_id,
					totalChunks,
					fileSize,
					uploadId,
					bucket,
					bucketPath,
					uploadedChunks = 0,
				} = uploadData;

				socket.on("connect", () => {
					this.uploadStatus = "Uploading File";
					startTime = Date.now();
					emitNextChunk(uploadedChunks);
				});

				socket.on("connect_error", (err) => {
					console.error("Socket connection error:", err);
					reject(err);
				});

				socket.on("send-chunk", (percent, uploadedChunksCount) => {
					const currentTime = Date.now();
					const timeTaken = (currentTime - startTime) / 1000;
					startTime = currentTime;

					const uploadSpeed = chunkSize / timeTaken;
					totalUploadedSize += chunkSize;
					const remainingSize = fileSize - totalUploadedSize;
					const estimatedTimeRemaining = remainingSize / uploadSpeed;

					this.timeRemaining = this.formatTime(estimatedTimeRemaining);

					this.progress = parseInt(percent);
					emitNextChunk(uploadedChunksCount);
				});

				socket.on("upload-completing", () => {
					this.progress = 100;
					this.timeRemaining = "0 sec";
				});

				socket.on("upload-complete", () => {
					socket.disconnect();
					this.uploadStatus = "File Uploaded";
					resolve(bucketPath);
				});

				socket.on("upload-failed", (error) => {
					console.error("Upload failed:", error);
					socket.disconnect();
					reject(new Error("Upload failed"));
				});

				const emitNextChunk = (uploadedChunksCount) => {
					const nextChunk = chunks.shift();
					if (nextChunk) {
						socket.emit(
							"store-chunk",
							nextChunk,
							_id,
							uploadedChunksCount,
							totalChunks,
							fileSize,
							uploadId,
							bucket,
							bucketPath
						);
					}
				};
			});
		},

		async calculateHashByIndex(file, index, { bucket, assetPath }) {
			try {
				this.fileData[index].progress = 1;
				this.fileData[index].timeRemaining = "1 sec";

				const arrayBuffer = await file.arrayBuffer();
				const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
				const hashArray = Array.from(new Uint8Array(hashBuffer));
				const fileHash = hashArray
					.map((b) => b.toString(16).padStart(2, "0"))
					.join("");

				const payload = {
					fileName: file.name,
					content: fileHash,
					size: file.size,
					bucket,
					bucketPath: `${assetPath}/${file.name}`,
				};

				const response = await checkFileExists(payload);

				if (response.status === 200) {
					const bucketPath = await this.chunkAndSendFileByIndex(
						response.data.data,
						arrayBuffer,
						index
					);
					return bucketPath;
				} else {
					throw new Error("File does not exist in the bucket");
				}
			} catch (error) {
				console.error("Error in calculateHashByIndex:", error);
				throw error;
			}
		},

		async chunkAndSendFileByIndex(uploadData, arrayBuffer, index) {
			return new Promise((resolve, reject) => {
				const chunkSize = 5 * 1024 * 1024;
				const chunks = [];
				let offset = 0;

				while (offset < arrayBuffer.byteLength) {
					const end = Math.min(offset + chunkSize, arrayBuffer.byteLength);
					chunks.push(arrayBuffer.slice(offset, end));
					offset = end;
				}

				const socket = io("https://awsmultipart.hire3x.com/", {
					transports: ["websocket"],
					withCredentials: true,
				});

				let startTime = null;
				let totalUploadedSize = 0;

				const {
					_id,
					totalChunks,
					fileSize,
					uploadId,
					bucket,
					bucketPath,
					uploadedChunks = 0,
				} = uploadData;

				socket.on("connect", () => {
					startTime = Date.now();
					emitNextChunk(uploadedChunks);
				});

				socket.on("connect_error", (err) => {
					console.error("Socket connection error:", err);
					reject(err);
				});

				socket.on("send-chunk", (percent, uploadedChunksCount) => {
					const currentTime = Date.now();
					const timeTaken = (currentTime - startTime) / 1000;
					startTime = currentTime;

					const uploadSpeed = chunkSize / timeTaken;
					totalUploadedSize += chunkSize;
					const remainingSize = fileSize - totalUploadedSize;
					const estimatedTimeRemaining = remainingSize / uploadSpeed;

					this.fileData[index].timeRemaining = this.formatTime(
						estimatedTimeRemaining
					);
					this.fileData[index].progress = parseInt(percent);
					emitNextChunk(uploadedChunksCount);
				});

				socket.on("upload-completing", () => {
					this.fileData[index].progress = 100;
					this.fileData[index].timeRemaining = "0 sec";
				});

				socket.on("upload-complete", () => {
					socket.disconnect();
					resolve(bucketPath);
				});

				socket.on("upload-failed", (error) => {
					console.error("Upload failed:", error);
					socket.disconnect();
					reject(new Error("Upload failed"));
				});

				const emitNextChunk = (uploadedChunksCount) => {
					const nextChunk = chunks.shift();
					if (nextChunk) {
						socket.emit(
							"store-chunk",
							nextChunk,
							_id,
							uploadedChunksCount,
							totalChunks,
							fileSize,
							uploadId,
							bucket,
							bucketPath
						);
					}
				};
			});
		},

		formatTime(seconds) {
			const h = Math.floor(seconds / 3600);
			const m = Math.floor((seconds % 3600) / 60);
			const s = Math.floor(seconds % 60);

			const parts = [];
			if (h > 0) parts.push(`${h} h`);
			if (m > 0) parts.push(`${m} min`);
			if (s > 0 || parts.length === 0) parts.push(`${s} sec`);
			return parts.join(" ");
		},
	},
};
